import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { Flex, IllustratedMessage, Loader, SuccessIcon, Typography } from '@percent/lemonade'

import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/cause-dashboard/common/hooks'
import type { SurveyProductDetail } from '@percent/cause-dashboard/services/surveys/surveyService.types'
import { DiscountRedemptionSurveyForm } from './discountRedemptionSurveyForm/DiscountRedemptionSurveyForm'
import { SurveyEventName } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/causeDashboardAnalytics.types'

export function DiscountRedemptionSurveyPage() {
  const { token } = useParams<{ token: string }>()
  const { search } = useLocation()
  const { t } = useTranslation()
  const { surveyService } = useServices()
  const analytics = useCausesPortalAnalytics()
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const params = new URLSearchParams(search)
  const queryParams = {
    validationSubmissionId: params.get('validationSubmissionId'),
    response: params.get('response')
  }

  const [{ data: surveyResponse, isLoading, errorMessage }] = useQuery(surveyService.getSurvey, token ?? '')
  const products = surveyResponse?.data?.data?.products as SurveyProductDetail[] | undefined

  useEffect(() => {
    if (products && products.length > 0) {
      analytics.page(SurveyEventName.DISCOUNT_REDEMPTION_SURVEY_VIEWED, {
        productCount: products.length,
        token
      })
    }
  }, [products, analytics, token])

  if (!token) {
    return (
      <Flex justify="center" align="center" mih="100vh" p="xl">
        <IllustratedMessage
          illustration="general-error"
          title={t('survey.discountRedemption.errors.title')}
          description={t('survey.discountRedemption.errors.message')}
          cta={{
            label: t('survey.discountRedemption.actions.refresh'),
            onClick: () => window.location.reload()
          }}
        />
      </Flex>
    )
  }

  if (isLoading) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Loader />
      </Flex>
    )
  }

  if (errorMessage || !products || products.length === 0) {
    return (
      <Flex justify="center" align="center" mih="100vh" p="xl">
        <IllustratedMessage
          illustration="general-error"
          title={t('survey.discountRedemption.errors.title')}
          description={t('survey.discountRedemption.errors.message')}
          cta={{
            label: t('survey.discountRedemption.actions.refresh'),
            onClick: () => window.location.reload()
          }}
        />
      </Flex>
    )
  }

  if (submitSuccess) {
    return (
      <Flex justify="center" align="center" mih="100vh" p="xl">
        <Flex direction="column" align="center" gap="lg" maw={480}>
          {SuccessIcon}
          <Typography variant="h3">{t('survey.discountRedemption.success.title')}</Typography>
          <Typography variant="default">{t('survey.discountRedemption.success.message')}</Typography>
        </Flex>
      </Flex>
    )
  }

  return (
    <DiscountRedemptionSurveyForm
      products={products}
      token={token}
      queryParams={queryParams}
      onSuccess={() => setSubmitSuccess(true)}
    />
  )
}
